import React, { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { StepProgress } from '@/components/ui/step-progress';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { renderTextWithLinks } from '@/utils/renderTextWithLinks';
import { cn } from '@/lib/utils';

interface PickProgressBarProps {
  answeredQuestions: boolean[];
  tiebreakerIndices?: number[];
  onSubmit: () => void;
  isSubmitting: boolean;
  className?: string;
  confirmationLogoUrl?: string;
  showTermsCheckbox?: boolean;
  submitMessage?: string;
  confirmationMessage?: string;
}

/**
 * Sticky gameplay footer — Figma 10203:52996
 * Progress always visible; outline CTA only when all answers are valid.
 */
const PickProgressBar: React.FC<PickProgressBarProps> = ({
  answeredQuestions,
  tiebreakerIndices = [],
  onSubmit,
  isSubmitting,
  className = '',
  confirmationLogoUrl,
  showTermsCheckbox = false,
  submitMessage,
  confirmationMessage,
}) => {
  const { t } = useTranslation();
  const confirmationBody =
    typeof confirmationMessage === 'string' && confirmationMessage.trim() !== ''
      ? confirmationMessage.trim()
      : t('actions.entryConfirmationMessage');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const wasSubmittingRef = useRef(false);

  const totalQuestions = answeredQuestions.length;
  const answeredCount = answeredQuestions.filter(Boolean).length;
  const allAnswered = answeredCount === totalQuestions && totalQuestions > 0;
  const canSubmit = allAnswered && !isSubmitting;
  const showSubmit = canSubmit || isSubmitting;
  const canConfirm = showTermsCheckbox ? isAgreed && !isSubmitting : !isSubmitting;

  const handleSubmitClick = () => {
    if (!canSubmit) return;
    setShowConfirmation(true);
  };

  const handleClose = () => {
    setShowConfirmation(false);
    setIsAgreed(false);
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    onSubmit();
  };

  // Keep drawer open while submitting (Figma 10203:53975); close when it finishes.
  useEffect(() => {
    if (isSubmitting) {
      wasSubmittingRef.current = true;
      return;
    }
    if (wasSubmittingRef.current && showConfirmation) {
      wasSubmittingRef.current = false;
      handleClose();
    }
  }, [isSubmitting, showConfirmation]);

  return (
    <>
      <div
        className="fixed inset-x-0 bottom-0 z-40 flex w-full justify-center"
        data-test-id="pick-progress-bar"
      >
        <div
          className={cn(
            'flex w-full flex-col gap-6 border-t-2 border-border bg-surface px-6 pb-8 pt-6 shadow-[0_0_16px_rgba(18,18,18,0.05)]',
            className
          )}
        >
          <StepProgress
            steps={answeredQuestions}
            className="overflow-x-auto"
            renderStep={(index, isComplete) => {
              if (!tiebreakerIndices.includes(index)) return null;
              return (
                <Star
                  className={`size-3.5 ${isComplete ? 'fill-primary text-primary' : 'text-white'}`}
                  data-test-tiebreaker
                />
              );
            }}
          />

          {showSubmit && (
            <div className="flex flex-col gap-2">
              {typeof submitMessage === 'string' && submitMessage.trim() !== '' && (
                <p
                  className="text-center font-sans text-xs text-muted-foreground"
                  data-test-id="submit-reminder-message"
                >
                  {submitMessage}
                </p>
              )}

              <Button
                onClick={handleSubmitClick}
                disabled={!canSubmit}
                variant="secondary"
                shape="pill"
                size="lg"
                loading={isSubmitting}
                className="w-full"
                data-test-id="submit-picks-button"
                data-test-enabled={canSubmit}
                showTrailingIcon={false}
              >
                {isSubmitting ? t('actions.submitting') : t('actions.submitPicks')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Spacer so content isn’t hidden under the fixed footer */}
      <div className={showSubmit ? 'h-44 shrink-0' : 'h-28 shrink-0'} aria-hidden="true" />

      {/* Confirmation drawer — Figma 10203:53683 (unchecked) / 10203:53975 (checked) */}
      <Dialog
        open={showConfirmation}
        onOpenChange={open => {
          if (!open) handleClose();
        }}
      >
        <DialogContent data-test-id="confirmation-panel" className="gap-5">
          <DialogTitle className="text-center">{t('actions.confirmationTitle')}</DialogTitle>

          <div className="flex w-full flex-col gap-5">
            <div className="flex w-full flex-col items-center justify-center gap-5 rounded-2xl bg-background px-6 py-6">
              <img
                src={confirmationLogoUrl ?? '/circus-daily-logo.svg'}
                alt={t('a11y.contestLogo')}
                className="h-8 w-auto object-contain"
              />

              <p
                className="w-full whitespace-pre-line text-center font-sans text-body-default text-white"
                data-test-id="confirmation-message"
              >
                {renderTextWithLinks(confirmationBody)}
              </p>

              {showTermsCheckbox && (
                <div data-test-id="terms-checkbox" className="w-full text-center">
                  <Checkbox
                    checked={isAgreed}
                    onCheckedChange={v => setIsAgreed(v === true)}
                    size="lg"
                    layout="vertical"
                    label={t('actions.termsAgreement')}
                  />
                </div>
              )}
            </div>

            <div className="flex w-full flex-col items-center gap-2">
              <Button
                onClick={handleConfirm}
                disabled={!canConfirm}
                variant="primary"
                shape="pill"
                size="lg"
                loading={isSubmitting}
                className="w-full"
                data-test-id="confirm-submit-entry"
                showTrailingIcon={false}
              >
                {t('actions.submitMyPicks')}
              </Button>

              <Button
                type="button"
                variant="link"
                onClick={handleClose}
                disabled={isSubmitting}
                className={cn('min-h-12 text-cta', isSubmitting && 'text-white/40 no-underline')}
                data-test-id="edit-my-picks"
                showTrailingIcon={false}
              >
                {t('actions.editMyPicks')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PickProgressBar;
